/**
 * Places, as the tools receive them.
 *
 * The server sends one string, since the facts packet carries text and nothing
 * else. Reading it is kept apart from the panel that draws it so neither has to
 * know about the other.
 */

export interface Place {
  id: string;
  name: string;
  /** Where it sits in the one world every region shares. */
  originX: number;
  originZ: number;
  width: number;
  height: number;
  /** Which layer it is on. Nought is the overworld. */
  layer: number;
}

/**
 * Splits what the server sent into places.
 *
 * JSON now, because a place is a rectangle as well as a name and the world is
 * meant to be one map. Anything unreadable comes back as no places at all
 * rather than throwing, since a panel that cannot draw the list is the panel
 * somebody would have used to fix it.
 */
export function placesFrom(text: string): Place[] {
  if (text.trim().length === 0) return [];

  try {
    const held: unknown = JSON.parse(text);
    if (!Array.isArray(held)) return [];

    return held.map((one) => {
      const place = one as Partial<Place>;
      const number = (value: unknown): number =>
        typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : 0;

      return {
        id: typeof place.id === 'string' ? place.id : '',
        name: typeof place.name === 'string' ? place.name : (place.id ?? ''),
        originX: number(place.originX),
        originZ: number(place.originZ),
        width: number(place.width),
        height: number(place.height),
        layer: number(place.layer),
      };
    });
  } catch {
    return [];
  }
}

/**
 * Which places sit on top of each other.
 *
 * The world is one map, so two regions covering the same ground is a mistake
 * rather than a layout: whichever loaded last would win, quietly.
 */
export function placesClashing(places: readonly Place[]): Place[] {
  return places.filter((one) =>
    places.some(
      (other) =>
        other.id !== one.id &&
        // Only on the same layer: a cellar under a house is not a clash.
        other.layer === one.layer &&
        one.width > 0 &&
        one.height > 0 &&
        other.width > 0 &&
        other.height > 0 &&
        one.originX < other.originX + other.width &&
        other.originX < one.originX + one.width &&
        one.originZ < other.originZ + other.height &&
        other.originZ < one.originZ + one.height,
    ),
  );
}
